import { useEffect, useState, useRef } from 'react'
import { styleRefsApi } from '../lib/api'
import type { StyleReference } from '../types'

export function StyleReferences() {
  const [styleRefs, setStyleRefs] = useState<StyleReference[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    styleRefsApi
      .getAll()
      .then(setStyleRefs)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = reader.result as string
        const styleRef = await styleRefsApi.create({
          name: file.name.replace(/\.[^/.]+$/, ''),
          description: '',
          imageBase64: base64,
        })
        setStyleRefs((prev) => [styleRef, ...prev])
        setUploading(false)
      }
      reader.onerror = () => {
        setError('Failed to read file')
        setUploading(false)
      }
      reader.readAsDataURL(file)
    } catch (err: any) {
      setError(err.message)
      setUploading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this style reference?')) return
    try {
      await styleRefsApi.delete(id)
      setStyleRefs((prev) => prev.filter((ref) => ref.id !== id))
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl md:text-4xl font-semibold text-text-main leading-tight">
            Style References
          </h1>
          <p className="text-text-muted text-base">
            Save your favorite styles to use in generations
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn btn-primary"
        >
          <span className="material-symbols-outlined text-[20px]">
            {uploading ? 'hourglass_empty' : 'upload'}
          </span>
          <span>{uploading ? 'Uploading...' : 'Upload Style'}</span>
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Styles Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl bg-surface-muted animate-pulse"
            />
          ))}
        </div>
      ) : styleRefs.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-text-light mb-4 block">
            palette
          </span>
          <h3 className="text-lg font-medium mb-2">No style references yet</h3>
          <p className="text-text-muted mb-6">
            Upload images that inspire the look you want
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary"
          >
            <span className="material-symbols-outlined text-lg">upload</span>
            Upload Style
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {styleRefs.map((styleRef) => (
            <div
              key={styleRef.id}
              className="group relative aspect-square rounded-xl overflow-hidden shadow-card"
            >
              <img
                src={styleRef.thumbnailUrl ?? styleRef.imageUrl}
                alt={styleRef.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                <div className="flex justify-end">
                  <button
                    onClick={() => handleDelete(styleRef.id)}
                    className="p-2 rounded-lg bg-white/90 hover:bg-red-50 text-red-600"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
                <div>
                  <p className="font-medium text-white">{styleRef.name}</p>
                  {styleRef.description && (
                    <p className="text-white/70 text-sm truncate">
                      {styleRef.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
